import { useState, type ReactElement, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, FolderPlus, Plus, UsersRound } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { usePermissions } from '@/auth/usePermissions';
import { getFamilyMemberFullName } from '@/features/families/mappers';
import { familyQueries } from '@/features/family-record/queries';
import { patientQueries } from '@/features/patient-record/queries';
import { AddToFamilyDialog } from '@/features/patient-record/components/AddToFamilyDialog';
import type { FamilyMemberViewModel, FamilySummaryViewModel } from '@/models/family';
import type { PatientViewModel } from '@/models/patient';

import { Panel } from './Panel';

interface FamilyPanelProps {
  readonly patient: PatientViewModel;
  readonly dragHandle?: ReactNode;
}

const MEMBER_PREVIEW_LIMIT = 4;

/**
 * Right-rail Family card on the patient record. Owns both states:
 *
 * - **Linked**: family name, member count, a short preview list of other
 *   members with deep-links to their patient records, and an "Open family
 *   record" button.
 * - **Unlinked**: two write CTAs (gated on `canCreateFamilies`) — create a
 *   new family with this patient as proband, or attach the patient to an
 *   existing family via {@link AddToFamilyDialog}.
 *
 * Membership is server-driven via `GET /persons/{guid}/families`
 * (PTV2-1160). A person may be in more than one family; today we render
 * the first summary from the page. If product needs a multi-family
 * disambiguator, add it here.
 */
export function FamilyPanel({ patient, dragHandle }: FamilyPanelProps): ReactElement {
  const { t } = useTranslation();
  const familiesQuery = useQuery(patientQueries.families(patient.guid));

  return (
    <Panel
      icon={<UsersRound className="h-3.5 w-3.5" aria-hidden="true" />}
      title={t('patient.sidePanels.family')}
      collapsible
      dragHandle={dragHandle}
    >
      <div className="px-4 py-3">
        {familiesQuery.isPending ? (
          <p className="text-[12.5px] text-muted-foreground">
            {t('patient.sections.family.loading')}
          </p>
        ) : familiesQuery.isError ? (
          <div className="flex flex-col items-start gap-1.5">
            <p className="text-[12.5px] text-destructive">
              {t('patient.sections.family.error.message')}
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => {
                void familiesQuery.refetch();
              }}
            >
              {t('routes.patient.error.retry')}
            </Button>
          </div>
        ) : (() => {
          const [firstFamily] = familiesQuery.data;
          if (firstFamily === undefined) {
            return <UnlinkedBody patientGuid={patient.guid} />;
          }
          return <LinkedBody patientGuid={patient.guid} family={firstFamily} />;
        })()}
      </div>
    </Panel>
  );
}

interface LinkedBodyProps {
  readonly patientGuid: string;
  readonly family: FamilySummaryViewModel;
}

function LinkedBody({ patientGuid, family }: LinkedBodyProps): ReactElement {
  const { t } = useTranslation();
  const membersQuery = useQuery(familyQueries.members(family.guid));

  // Prefer the loaded members list (authoritative) for the count; fall
  // back to the summary. The panel only renders in the "linked" branch,
  // so the current patient is by definition at least one member — never
  // display "0 members" even if both sources report empty.
  const members: readonly FamilyMemberViewModel[] = membersQuery.data ?? [];
  const otherMembers = members.filter((m) => m.personGuid !== patientGuid);
  // Preview only patient members, but the overflow count still covers
  // everyone not shown — including the non-patient members we never preview.
  const patientMembers = otherMembers.filter((m) => m.isPatient);
  const preview = patientMembers.slice(0, MEMBER_PREVIEW_LIMIT);
  const remaining = otherMembers.length - preview.length;
  const trimmedName = family.name.trim();
  const familyName = trimmedName.length > 0 ? trimmedName : t('routes.family.unnamedFamily');
  const emptyName = t('patient.demographics.emptyValue');
  const memberCount = Math.max(members.length, family.memberCount, 1);
  const showOnlyMemberHint = !membersQuery.isPending && preview.length === 0;

  return (
    <div className="flex flex-col gap-2.5">
      <div className="min-w-0 space-y-0.5">
        <p className="text-[12.5px] font-medium text-foreground truncate">{familyName}</p>
        <p className="text-[11.5px] text-muted-foreground tabular-nums">
          {t('patient.sections.family.linked.memberCount', { count: memberCount })}
        </p>
      </div>

      {showOnlyMemberHint && (
        <p className="text-[11.5px] text-muted-foreground italic">
          {t('patient.sections.family.linked.noOtherMembers')}
        </p>
      )}

      {preview.length > 0 && (
        <ul className="space-y-0.5">
          {preview.map((member) => {
            const name = getFamilyMemberFullName(member) ?? emptyName;
            return (
              <li
                key={member.id}
                className="flex items-baseline justify-between gap-2 text-xs"
              >
                <span className="min-w-0 truncate">
                  {member.isPatient && member.personGuid !== null ? (
                    <Link
                      to={`/patients/${member.personGuid}`}
                      className="text-primary hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                    >
                      {name}
                    </Link>
                  ) : (
                    <span className="text-foreground">{name}</span>
                  )}
                </span>
                <span className="shrink-0 text-[10.5px] text-muted-foreground">
                  {t(`family.cascadeRole.${member.cascadeRole}`)}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {remaining > 0 && (
        <Link
          to={`/families/${family.guid}`}
          className="text-[11.5px] font-medium text-primary hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        >
          {t('patient.sections.family.linked.seeAll', { count: remaining })}
        </Link>
      )}

      <Button asChild size="sm" variant="ghost" className="h-7 px-2 text-xs gap-1 text-primary self-start">
        <Link to={`/families/${family.guid}`}>
          <ExternalLink className="h-3 w-3" aria-hidden="true" />
          {t('patient.sections.family.linked.openFamilyRecord')}
        </Link>
      </Button>
    </div>
  );
}

interface UnlinkedBodyProps {
  readonly patientGuid: string;
}

function UnlinkedBody({ patientGuid }: UnlinkedBodyProps): ReactElement {
  const { t } = useTranslation();
  const { canCreateFamilies } = usePermissions();
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <div className="flex flex-col items-start gap-2">
      <p className="text-[12.5px] text-muted-foreground italic">
        {t('patient.sections.family.unlinked.body')}
      </p>
      {canCreateFamilies && (
        <div className="flex flex-col items-start gap-0.5 -ms-2">
          <Button
            asChild
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs gap-1 text-primary"
          >
            <Link to={`/families/new?probandPersonId=${encodeURIComponent(patientGuid)}`}>
              <FolderPlus className="h-3 w-3" aria-hidden="true" />
              {t('patient.sections.family.unlinked.createWithThisPatient')}
            </Link>
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs gap-1 text-primary"
            onClick={() => {
              setPickerOpen(true);
            }}
          >
            <Plus className="h-3 w-3" aria-hidden="true" />
            {t('patient.sections.family.unlinked.addToExisting')}
          </Button>
        </div>
      )}

      {pickerOpen && (
        <AddToFamilyDialog
          patientGuid={patientGuid}
          onCancel={() => {
            setPickerOpen(false);
          }}
          onSuccess={() => {
            setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
}
